import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronRightIcon, trashIcon } from '@progress/kendo-svg-icons';
import type { DlpLabelModel } from 'services/DlpConfiguration/DlpConfigurationService';

interface LabelEditorRowProps {
    label: DlpLabelModel;
    index: number;
    parents: DlpLabelModel[];
    hasChildren: boolean;
    readOnly?: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onMove: (direction: -1 | 1) => void;
    onUpdate: (patch: Partial<DlpLabelModel>) => void;
    onRemove: () => void;
}

export function LabelEditorRow({ label, index, parents, hasChildren, canMoveUp, canMoveDown, onMove, onUpdate, onRemove, readOnly = false }: LabelEditorRowProps) {
    const { t } = useTranslation();
    const [idText, setIdText] = useState(label.id);
    useEffect(() => setIdText(label.id), [label.id]);
    if (readOnly) return (
        <div className={`label-row ${label.parent_id ? 'label-row-child' : ''}`} style={{ gridTemplateColumns: '1fr 2fr 2fr' }}>
            <input className="lm-input sm" readOnly value={label.id}
                aria-label={t('dlpConfiguration.project.labelHierarchy.idInput', { label: label.id || `#${index + 1}` })} />
            <input className="lm-input sm" readOnly value={label.name}
                aria-label={t('dlpConfiguration.project.labelHierarchy.nameInput', { label: label.id || `#${index + 1}` })} />
            <input className="lm-input sm" readOnly value={label.parent_id ?? t('dlpConfiguration.project.labelHierarchy.noParent')}
                aria-label={t('dlpConfiguration.project.labelHierarchy.parentInput', { label: label.id || `#${index + 1}` })} />
        </div>
    );
    // Commit ID changes on blur, not each keystroke, so clearing/retyping a parent
    // doesn't temporarily detach all children. Rename updates their references once.
    const commitId = () => {
        const id = idText.trim();
        if (!id && hasChildren) {
            setIdText(label.id); // Never detach a whole group by clearing its parent ID.
            return;
        }
        setIdText(id);
        if (id !== label.id) onUpdate({ id });
    };
    const reference = label.id || `#${index + 1}`;
    const candidates = parents.filter(parent => parent.id !== label.id);
    const missingParent = label.parent_id && !candidates.some(parent => parent.id === label.parent_id);
    return (
        <div className={`label-row ${label.parent_id ? 'label-row-child' : ''}`}>
            <div className="reorder">
                <button type="button" aria-label={t('dlpConfiguration.project.labelHierarchy.moveUp', { label: reference })} onClick={() => onMove(-1)} disabled={!canMoveUp}>
                    <SvgIcon icon={chevronRightIcon} size="xsmall" className="rotate-270" />
                </button>
                <button type="button" aria-label={t('dlpConfiguration.project.labelHierarchy.moveDown', { label: reference })} onClick={() => onMove(1)} disabled={!canMoveDown}>
                    <SvgIcon icon={chevronRightIcon} size="xsmall" className="rotate-90" />
                </button>
            </div>
            <input className="lm-input sm" placeholder={t('dlpConfiguration.project.labelId')}
                aria-label={t('dlpConfiguration.project.labelHierarchy.idInput', { label: reference })}
                value={idText} onChange={e => setIdText(e.target.value)} onBlur={commitId}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            <input className="lm-input sm" placeholder={t('dlpConfiguration.project.labelName')}
                aria-label={t('dlpConfiguration.project.labelHierarchy.nameInput', { label: reference })}
                value={label.name} onChange={e => onUpdate({ name: e.target.value })} />
            <select className="lm-input sm" value={label.parent_id ?? ''}
                aria-label={t('dlpConfiguration.project.labelHierarchy.parentInput', { label: reference })}
                title={hasChildren ? t('dlpConfiguration.project.labelHierarchy.parentLocked') : undefined}
                onChange={e => onUpdate({ parent_id: e.target.value || null })}>
                <option value="">{t('dlpConfiguration.project.labelHierarchy.noParent')}</option>
                {missingParent && <option value={label.parent_id!} disabled>{t('dlpConfiguration.project.labelHierarchy.invalidParent', { parent: label.parent_id })}</option>}
                {candidates.map(parent => <option key={parent.id} value={parent.id} disabled={hasChildren}>{parent.name || parent.id} ({parent.id})</option>)}
            </select>
            <button type="button" className="icon-btn" aria-label={t('dlpConfiguration.project.labelHierarchy.remove', { label: reference })} onClick={onRemove}>
                <SvgIcon icon={trashIcon} size="small" />
            </button>
        </div>
    );
}
